import { extractFencedCodeSegments } from './extractFencedCodeSegments';
import { stripMarkdownTables } from './stripMarkdownTables';

/**
 * OpenAI's `audio/speech` endpoint rejects input longer than 4096 characters, so this is a hard
 * upstream limit rather than a product decision.
 */
export const SPEECH_MAX_CHARS = 4096;

export type SpeakableText = {
  text: string;
  /** True when the answer was longer than the endpoint accepts and only the opening was kept. */
  truncated: boolean;
};

/**
 * Markdown that carries no sound: emphasis markers, heading hashes, list bullets, blockquote
 * arrows, rules and inline backticks. Links keep their label and lose their URL — reading a URL
 * aloud is noise.
 */
function stripMarkdownSyntax(markdown: string): string {
  return (
    markdown
      // Images have no spoken equivalent; their alt text is usually a filename, not a sentence.
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/^\s{0,3}#{1,6}\s+/gm, '')
      .replace(/^\s{0,3}>\s?/gm, '')
      .replace(/^\s{0,3}([-*_])(\s*\1){2,}\s*$/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      .replace(/`([^`]*)`/g, '$1')
      .replace(/(\*\*|__)(.*?)\1/g, '$2')
      .replace(/(\*|_)(.*?)\1/g, '$2')
      .replace(/~~(.*?)~~/g, '$1')
      .replace(/<[^>]+>/g, '')
  );
}

/**
 * Cut at the last sentence end, or failing that the last word boundary, so a truncated answer
 * stops somewhere a listener recognises as an ending rather than mid-word.
 */
function cutAtBoundary(text: string, limit: number): string {
  const head = text.slice(0, limit);
  const lastSentence = Math.max(head.lastIndexOf('. '), head.lastIndexOf('! '), head.lastIndexOf('? '));

  if (lastSentence > limit / 2) {
    return head.slice(0, lastSentence + 1);
  }

  const lastWord = head.lastIndexOf(' ');
  return (lastWord > 0 ? head.slice(0, lastWord) : head).trim();
}

/**
 * Reduce an askRichard answer to the prose a human would actually want read aloud.
 *
 * An answer mixes narration with markdown tables, SQL fences and chart config. Fed raw to TTS
 * those become minutes of spoken pipes, braces and column names. `extractFencedCodeSegments`
 * already separates fenced blocks from prose for rendering; here it is used the other way round —
 * keep the markdown segments, drop the fenced ones — and `stripMarkdownTables` removes the GFM
 * tables that are not fenced at all.
 *
 * Known v1 rough edge: an answer whose narration is only "as shown in the table below" will be
 * read aloud pointing at nothing audible. Left alone until real usage shows how often it happens.
 */
export function extractSpeakableText(markdown: string): SpeakableText {
  if (!markdown?.trim()) {
    return { text: '', truncated: false };
  }

  const prose = extractFencedCodeSegments(markdown)
    .filter((segment) => segment.kind === 'markdown')
    .map((segment) => segment.content)
    .join('\n\n');

  const text = stripMarkdownSyntax(stripMarkdownTables(prose))
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{2,}/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n')
    .trim();

  if (text.length <= SPEECH_MAX_CHARS) {
    return { text, truncated: false };
  }

  return { text: cutAtBoundary(text, SPEECH_MAX_CHARS), truncated: true };
}
